import ChevronRight from '@assets/icons/chevron-right.svg';
import React from 'react';
import { formatCurrency } from '@/utils/money';
import { ListOption } from '@/components/list-item';
import { PaymentBatchLineItemType } from '@/enums';
import { PaymentHistoryDetailListItemFieldsFragment } from '@/types';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import {
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { useTheme } from '@/contexts/theme-context';

export const PAYMENT_HISTORY_DETAIL_LIST_ITEM_HEIGHT = 54;

export type PaymentHistoryDetailItemOption = ListOption<{
  paymentBatchLineItem: PaymentHistoryDetailListItemFieldsFragment;
}>;

interface Props {
  currencyCode: string | null;
  option: PaymentHistoryDetailItemOption;
  setSelectedListItem: (
    paymentBatchLineItem: PaymentHistoryDetailListItemFieldsFragment,
  ) => void;
}

export const PaymentHistoryDetailListItem: React.FC<Props> = ({
  currencyCode,
  option,
  setSelectedListItem,
}) => {
  const theme = useTheme();
  const baseStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(styles.base, [
    styles.base,
    styles.baseLarge,
  ]);

  const { paymentBatchLineItem } = option.metadata!;

  const isCredit =
    paymentBatchLineItem.type === PaymentBatchLineItemType.CREDIT;

  const isDisabled =
    paymentBatchLineItem.credit_memo === null &&
    paymentBatchLineItem.order_invoice === null;

  return (
    <TouchableOpacity
      disabled={isDisabled}
      onPress={() => setSelectedListItem(paymentBatchLineItem)}
      style={baseStyle}
    >
      <Text style={[TYPOGRAPHY.h3, styles.text]}>{option.title}</Text>
      <View style={styles.textWithIcon}>
        <Text style={[TYPOGRAPHY.h3, styles.text]}>
          {`${isCredit ? '-' : ''}${formatCurrency(
            paymentBatchLineItem.amount_in_micros,
            currencyCode,
          )}`}
        </Text>
        {!isDisabled ? (
          <ChevronRight fill={theme.colors.inactiveFill} height="12" />
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    height: PAYMENT_HISTORY_DETAIL_LIST_ITEM_HEIGHT,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
  },
  baseLarge: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  text: {
    fontSize: 16,
  },
  textWithIcon: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
  },
});
